import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { money, pushToast, useAsync } from "../lib";
import { Empty, Skeleton, LoginGate } from "../ui";
import SlipUpload from "../SlipUpload";
import iconKbank from "../../assets/bankicon/004.webp";
import iconKtb from "../../assets/bankicon/006.webp";
import iconBbl from "../../assets/bankicon/002.webp";
import iconTtb from "../../assets/bankicon/011.webp";
import iconScb from "../../assets/bankicon/014.webp";
import iconBay from "../../assets/bankicon/025.webp";
import iconGsb from "../../assets/bankicon/030.webp";
import iconBaac from "../../assets/bankicon/034.webp";
import iconTrue from "../../assets/bankicon/true.webp";

const BANK_ICON = {
  kbank: iconKbank, "004": iconKbank, ktb: iconKtb, "006": iconKtb,
  bbl: iconBbl, "002": iconBbl, ttb: iconTtb, "011": iconTtb,
  scb: iconScb, "014": iconScb, krungsri: iconBay, "025": iconBay,
  gsb: iconGsb, "030": iconGsb, baac: iconBaac, "034": iconBaac, true: iconTrue,
};

// คัดลอกลงคลิปบอร์ด — ใช้ execCommand สำรองเมื่อ navigator.clipboard ใช้ไม่ได้ (http / บางเบราว์เซอร์)
async function copyText(text) {
  const value = String(text);
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // ตกไปใช้วิธีสำรอง
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function groupNumber(n, key) {
  const d = String(n || "").replace(/\D/g, "");
  if (key === "true" && d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 4)}-${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `${d.slice(0, 4)}-${d.slice(4, 8)}-${d.slice(8)}`;
  return String(n || "");
}

function BankCard({ bank, copied, onCopy }) {
  const icon = BANK_ICON[bank.key];
  return (
    <div className="lx-dp-card">
      <div className="lx-dp-card-top">
        {icon ? <img className="lx-dp-logo" src={icon} alt="" /> : <span className="lx-dp-logo ph"><Icon icon="solar:card-bold-duotone" width={24} height={24} /></span>}
        <div className="lx-dp-bankname">
          <b>{bank.name}</b>
          {bank.auto && (
            <span className="lx-dp-auto">
              <Icon icon="solar:bolt-bold" width={11} height={11} /> เติมอัตโนมัติ
            </span>
          )}
        </div>
      </div>
      <div className="lx-dp-label">เลขบัญชี</div>
      <div className="lx-dp-number lx-mono">{groupNumber(bank.number, bank.key)}</div>
      <div className="lx-dp-owner">
        <Icon icon="solar:user-rounded-bold-duotone" width={15} height={15} />
        <span>{bank.owner || "-"}</span>
      </div>
      <button className={`lx-dp-copy ${copied ? "done" : ""}`} onClick={onCopy}>
        <Icon icon={copied ? "solar:check-circle-bold" : "solar:copy-bold"} width={17} height={17} />
        {copied ? "คัดลอกแล้ว" : "คัดลอกเลขบัญชี"}
      </button>
    </div>
  );
}

const BANK_NAME = {
  scb: "ไทยพาณิชย์", kbank: "กสิกรไทย", ktb: "กรุงไทย", bbl: "กรุงเทพ",
  ttb: "ทีทีบี", gsb: "ออมสิน", krungsri: "กรุงศรี", baac: "ธ.ก.ส.",
  true: "ทรูมันนี่ วอลเล็ท", "014": "ไทยพาณิชย์", "004": "กสิกรไทย",
  "006": "กรุงไทย", "002": "กรุงเทพ", "011": "ทีทีบี", "030": "ออมสิน",
};

export default function DepositPage({ onDone }) {
  const { user, isAuthenticated } = useAuth();
  const nav = useNavigate();
  const uid = user?.id;
  const { data: banks, loading } = useAsync(
    () => (isAuthenticated ? lottoApi.depositBanks() : Promise.resolve([])),
    [isAuthenticated],
  );
  const { data: gsbAccounts } = useAsync(
    () => (isAuthenticated ? lottoApi.gsbAccounts() : Promise.resolve([])),
    [isAuthenticated],
  );
  const { data: history } = useAsync(
    () => (uid ? lottoApi.depositHistory(uid) : Promise.resolve([])),
    [uid],
  );
  const [copiedKey, setCopiedKey] = useState(null);
  const [tab, setTab] = useState("transfer");
  const { data: slipEnabled } = useAsync(
    () => (isAuthenticated ? lottoApi.slipSetting() : Promise.resolve(false)),
    [isAuthenticated],
  );
  // ปิดแท็บสลิปจากแอดมิน → กลับมาแท็บโอนเงินอัตโนมัติ
  const activeTab = tab === "slip" && slipEnabled ? "slip" : "transfer";

  // บัญชีรับฝากทั้งหมดที่แสดงให้ลูกค้า (ตัดแถวสวิตช์ gsbauto และแถวที่ไม่มีเลขบัญชีออก)
  const activeBanks = useMemo(() => {
    const normal = (banks || [])
      .filter(
        (b) =>
          String(b.status).toUpperCase() === "ACTIVE" &&
          b.bankname !== "gsbauto" &&
          String(b.banknumber || "").trim(),
      )
      .map((b) => ({
        id: `bank-${b.BanklistID || b.banknumber}`,
        key: b.bankname,
        name: BANK_NAME[b.bankname] || b.bankname,
        number: b.banknumber,
        owner: b.bankownername,
        auto: ["scb", "true"].includes(String(b.bankname)),
      }));
    const gsb = (gsbAccounts || []).map((g) => ({
      id: `gsb-${g.id}`,
      key: "gsb",
      name: "ออมสิน",
      number: g.accountNumber,
      owner: g.accountName,
      auto: g.autoTopup !== false,
    }));
    return [...gsb, ...normal];
  }, [banks, gsbAccounts]);

  if (!isAuthenticated)
    return (
      <LoginGate
        icon="fluent-emoji-flat:money-with-wings"
        title="เข้าสู่ระบบก่อนฝากเงิน"
        desc="ฝากเงินเข้าบัญชีเล่นหวยของคุณอย่างปลอดภัย"
        perks={["ฝากด้วยบัญชีที่ผูกไว้","เครดิตเข้าอัตโนมัติ","ดูประวัติการฝากย้อนหลัง"]}
        onLogin={() => nav("/auth")}
      />
    );

  const copy = async (id, text) => {
    const ok = await copyText(text);
    if (ok) {
      setCopiedKey(id);
      pushToast("คัดลอกเลขบัญชีแล้ว", "ok", 1800);
      setTimeout(() => setCopiedKey((k) => (k === id ? null : k)), 2000);
    } else {
      pushToast(`คัดลอกไม่ได้ — กดค้างที่เลขบัญชีเพื่อคัดลอก: ${text}`, "err", 5000);
    }
  };

  return (
    <div className="vx-money" style={{ marginTop: 18 }}>
      <header className="lx-card vx-pagehead vx-money-head">
        <div className="vx-money-copy">
          <span className="vx-kicker">
            <Icon icon="fluent-emoji-flat:money-bag" width={16} height={16} />
            เติมเครดิต
          </span>
          <h1 className="lx-h1">ฝากเงิน</h1>
          <p>โอนเงินจากบัญชีที่ผูกไว้ แล้วเครดิตเข้าอัตโนมัติ</p>
        </div>
        <div className="vx-money-art" aria-hidden="true">
          <Icon icon="fluent-emoji-flat:money-bag" className="a1" />
          <Icon icon="fluent-emoji-flat:coin" className="a2" />
          <Icon icon="fluent-emoji-flat:sparkles" className="a3" />
        </div>
      </header>

      {slipEnabled && (
        <div className="lx-dp-tabs">
          <button className={activeTab === "transfer" ? "on" : ""} onClick={() => setTab("transfer")}>
            <Icon icon="solar:card-bold-duotone" width={18} height={18} /> โอนเข้าบัญชี
          </button>
          <button className={activeTab === "slip" ? "on" : ""} onClick={() => setTab("slip")}>
            <Icon icon="solar:bill-list-bold-duotone" width={18} height={18} /> อัพโหลดสลิป
          </button>
        </div>
      )}

      {activeTab === "slip" ? (
        <SlipUpload userId={uid} onDone={onDone} />
      ) : (
        <>
          <ol className="vx-dsteps">
            <li>
              <span className="vx-dstep-no">1</span>
              <Icon icon="fluent-emoji-flat:link" width={30} height={30} />
              <b>คัดลอกเลขบัญชี</b>
              <small>เลือกบัญชีรับฝากด้านล่าง</small>
            </li>
            <li>
              <span className="vx-dstep-no">2</span>
              <Icon icon="fluent-emoji-flat:credit-card" width={30} height={30} />
              <b>โอนเงิน</b>
              <small>จากบัญชีที่ผูกไว้</small>
            </li>
            <li>
              <span className="vx-dstep-no">3</span>
              <Icon icon="fluent-emoji-flat:alarm-clock" width={30} height={30} />
              <b>รับเครดิต</b>
              <small>เข้าอัตโนมัติ 1–3 นาที</small>
            </li>
          </ol>

          <div className="vx-linked">
            <span className="vx-linked-ic">
              <Icon icon="solar:shield-check-bold-duotone" width={24} height={24} />
            </span>
            <div className="vx-linked-tx">
              <small>บัญชีของคุณที่ผูกไว้ (ต้องโอนจากบัญชีนี้เท่านั้น)</small>
              <b>{user?.book_name || user?.name || "-"}</b>
              <span className="lx-mono">{groupNumber(user?.book_number)}</span>
            </div>
          </div>

          <section className="vx-block">
            <div className="vx-block-head">
              <div className="vx-block-title">
                <span className="vx-block-ic">
                  <Icon icon="solar:card-bold-duotone" width={18} height={18} />
                </span>
                <h2 className="lx-h2">บัญชีรับฝาก</h2>
              </div>
            </div>
            {loading && <Skeleton h={120} />}
            {!loading && !activeBanks.length && (
              <div className="lx-card">
                <Empty icon="solar:card-bold-duotone">ยังไม่มีบัญชีรับฝาก — กรุณาติดต่อแอดมิน</Empty>
              </div>
            )}
            <div className="lx-dp-grid">
              {activeBanks.map((b) => (
                <BankCard key={b.id} bank={b} copied={copiedKey === b.id} onCopy={() => copy(b.id, b.number)} />
              ))}
            </div>
          </section>
        </>
      )}

      <section className="vx-block">
        <div className="vx-block-head">
          <div className="vx-block-title">
            <span className="vx-block-ic cat-b">
              <Icon icon="solar:history-bold-duotone" width={18} height={18} />
            </span>
            <h2 className="lx-h2">ประวัติฝาก</h2>
          </div>
        </div>
        {!history?.length ? (
          <div className="lx-card">
            <Empty icon="solar:document-text-bold-duotone">ยังไม่มีรายการ</Empty>
          </div>
        ) : (
          <div className="vx-hist">
            {history.slice(0, 20).map((h, i) => (
              <div className="vx-hrow" key={h.topupHistoryID || i}>
                <span className="vx-hrow-ic in">
                  <Icon icon="solar:download-minimalistic-bold" width={20} height={20} />
                </span>
                <div className="vx-hrow-tx">
                  <b>ฝากเงิน</b>
                  <small>{String(h.created_at || "").replace("T", " ").slice(0, 16)}</small>
                </div>
                <div className="vx-hrow-end">
                  <b className="lx-mono in">+{money(h.amount)}</b>
                  {Number(h.BonusAmount) > 0 && <span className="lx-tag win">+โบนัส {money(h.BonusAmount)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
